import React, {
  useRef,
  useEffect,
  useState,
  useCallback,
} from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useStaticQuery, graphql } from 'gatsby';
import ReactHtmlParser from 'react-html-parser';

import Button150 from '../base/Button150';

import '~styles/modules/scrollytelling.scss';

const ScrolltellingContainer = styled.div`
  width: 100%;
  position: relative;
`;

const WrapperPadder = styled.div`
  width: 100%;
  height: 150vh;
  z-index: 0;
  display: block;
`;

const ImageFrame = styled.div`
  width: 100%;
  height: 100vh;
  position: sticky;
  top: 0;
  left: 0;
`;

const ImageFlipper = styled(Button150)`
  top: 10px;
  left: 10px;
  display: none;
`;

const ImageContainer = styled.div`
  position: relative;
`;

const Slide = styled.div`
  height: 125vh;
  width: 100%;
  position: relative;
  pointer-events: none;
  -webkit-transform: translate3d(0,0,0);
  &:last-of-type {
    margin-bottom: 25vh;
  }
  @media (max-width: 900px) {
    height: 150vh;
  }
`;

const SlideTopper = styled(Slide)`
  height: 50px
`;

const SlideContent = styled.div`
  max-width: 350px;
  width: 95%;
  padding: 1rem;
  background-color: white;
  border: 1px solid var(--globe-green);
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  border-radius: 3px;
  h4 {
    margin-bottom: 0.25rem;
  }
  p {
    font-size: 0.875rem;
    line-height: 1rem;
    text-align: left;
  }
`;

const Scrollytelling = ({ data }) => {
  const { scrollytelling } = data;

  const slidesData = useStaticQuery(graphql`
    query SlidesQuery { 
      file(name: {eq: "Slides"}) {
        childDataJson {
          slides {
            name
            text
            slide
          }
        }
      }
    }
  `);

  const slideRef0 = useRef();
  const slideRef1 = useRef();
  const slideRef2 = useRef();
  const slideRef3 = useRef();
  const slideRef4 = useRef();
  const slideRef5 = useRef();
  const slideRef6 = useRef();
  const slideRef7 = useRef();
  const slideRef8 = useRef();
  const slideRef9 = useRef();
  const slideRef10 = useRef();
  const slideRef11 = useRef();
  const slideRef12 = useRef();
  const slideRef13 = useRef();
  const slideRef14 = useRef();
  const slideRef15 = useRef();

  const [currentSlide, setCurrentSlide] = useState('slide0');
  const [showIllo, setShowIllo] = useState(false);
  const [buttonText, setButtonText] = useState('Show 1848');

  const slideRefs = [
    slideRef0,
    slideRef1,
    slideRef2,
    slideRef3,
    slideRef4,
    slideRef5,
    slideRef6,
    slideRef7,
    slideRef8,
    slideRef9,
    slideRef10,
    slideRef11,
    slideRef12,
    slideRef13,
    slideRef14,
    slideRef15,
  ];

  useEffect(() => {
    function handleIntersect(entries) {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setCurrentSlide(entry.target.getAttribute('data-slide'));
        }
      });
    }

    const options = {
      root: null,
      rootMargin: '0px',
    };

    slideRefs.forEach((slideRef) => {
      const observer = new IntersectionObserver(handleIntersect, options);
      if (slideRef.current) {
        observer.observe(slideRef.current);
      }
      return () => observer.unobserve(slideRef.current);
    });
  }, [
    slideRef0.current,
    slideRef1.current,
    slideRef2.current,
    slideRef3.current,
    slideRef4.current,
    slideRef5.current,
    slideRef6.current,
    slideRef7.current,
    slideRef8.current,
    slideRef9.current,
    slideRef10.current,
    slideRef11.current,
    slideRef12.current,
    slideRef13.current,
    slideRef14.current,
    slideRef15.current,
  ]);

  const flipImages = useCallback(() => {
    if (showIllo) {
      setButtonText('Show 1848');
    } else {
      setButtonText('Show 2022');
    }
    setShowIllo(!showIllo);
  }, [showIllo]);

  return (
    <div>
      <ScrolltellingContainer className={currentSlide}>
        <ImageFrame id="image__frame">
          <ImageFlipper text={buttonText} onClick={flipImages} />
          <ImageContainer
            id="image__pano"
            className="sticky-image"
          />

          <ImageContainer
            id="image__illo"
            className={`sticky-image ${showIllo === true ? '' : 'no-show'}`}
          />

          <ImageContainer
            id="image__halo"
            className={`sticky-image ${showIllo === false ? '' : 'no-show'}`}
          />

          <ImageContainer
            id="illo__halo"
            className={`sticky-image ${showIllo === true ? '' : 'no-show'}`}
          />
        </ImageFrame>
        <SlideTopper className="slide" ref={slideRef0} data-slide="slide-0" />
        {slidesData.file.childDataJson.slides.map((slide, index) => (
          <Slide className={`slide slide__num-${slide.slide}`} key={`slide${slide.name}`}>
            <SlideContent ref={slideRefs[index + 1]} data-slide={`slide-${slide.slide}`}>
              <h4 className="bentonsanscond-n6">{slide.name}</h4>
              <p className="bentonsanscond-n4">{ReactHtmlParser(slide.text)}</p>
            </SlideContent>
          </Slide>
        ))}
        <WrapperPadder id="scrollytelling__padding" ref={slideRef15} data-slide="slide-15" />
      </ScrolltellingContainer>
    </div>
  );
};

export default Scrollytelling;

Scrollytelling.propTypes = {
  data: PropTypes.shape({
    scrollytelling: PropTypes.string.isRequired,
  }).isRequired,
};
